/**
 * PORTAL COMPONENT - Renders in Mapbox GL popup portals outside React tree
 *
 * ⚠️  DO NOT use hooks that require context (useNavigate, useLocation, useContext, etc.)
 * ⚠️  All dependencies must be passed via props from the parent component
 */
import mapboxgl from 'mapbox-gl';
import type { Unit, IndustryType } from '@/types';
import { ClusterContent } from './shared/ClusterContent';
import { ShareButton } from './shared/ShareButton';

interface ClusterPopupProps {
  units: Unit[];
  allUnits?: Unit[]; // All units in the dataset for zoom calculation
  location: string;
  onViewAll: () => void;
  isMobile: boolean;
  map: mapboxgl.Map | null;
  filterActions: {
    setSelectedCompanyIds: (ids: string[]) => void;
    setSelectedGroupIds: (ids: string[]) => void;
    setSelectedIndustryTypes: (types: IndustryType[]) => void;
    setSelectedModels: (models: string[]) => void;
    saveFiltersForRestore: () => void;
    restoreSavedFilters: () => void;
    setShouldExpandAll: (value: boolean) => void;
  };
  hasSavedFilters: boolean;
  onClose: () => void;
  getShareableUrl?: () => string;
}

export function ClusterPopup({
  units,
  allUnits,
  onViewAll,
  map,
  filterActions,
  hasSavedFilters,
  onClose,
  getShareableUrl,
}: ClusterPopupProps) {
  // We'll use a simple wrapper around the shared component
  // The styling for the popup container is handled by Mapbox CSS

  return (
    <div className="w-[375px] h-[400px] flex flex-col bg-background rounded-xl overflow-hidden shadow-lg border border-border/50 relative group">
        <ShareButton
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          getShareUrl={getShareableUrl}
        />
        <ClusterContent
          units={units}
          allUnits={allUnits}
          isMobile={false} // Always false for desktop popup
          onClose={onClose}
          onViewAll={onViewAll}
          map={map}
          filterActions={filterActions}
          hasSavedFilters={hasSavedFilters}
        />
    </div>
  );
}
